// Task status workflow validation and transitions
export enum TaskStatus {
  Todo = 'Todo',
  InProgress = 'InProgress',
  InReview = 'InReview',
  Blocked = 'Blocked',
  Done = 'Done',
  Cancelled = 'Cancelled'
}

export enum TaskPriority {
  Lowest = 'Lowest',
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Highest = 'Highest',
  Critical = 'Critical'
}

export enum TaskUrgency {
  Low = 'Low',
  Normal = 'Normal',
  Urgent = 'Urgent'
}

// Valid status transitions
const statusTransitions: Record<TaskStatus, TaskStatus[]> = {
  [TaskStatus.Todo]: [TaskStatus.InProgress, TaskStatus.Blocked, TaskStatus.Cancelled],
  [TaskStatus.InProgress]: [TaskStatus.InReview, TaskStatus.Blocked, TaskStatus.Todo, TaskStatus.Cancelled],
  [TaskStatus.InReview]: [TaskStatus.Done, TaskStatus.InProgress, TaskStatus.Blocked, TaskStatus.Cancelled],
  [TaskStatus.Blocked]: [TaskStatus.Todo, TaskStatus.InProgress, TaskStatus.Cancelled],
  [TaskStatus.Done]: [TaskStatus.InReview], // Can only reopen to review
  [TaskStatus.Cancelled]: [TaskStatus.Todo] // Can only restart
};

export function isValidStatusTransition(fromStatus: TaskStatus, toStatus: TaskStatus): boolean {
  if (fromStatus === toStatus) { return true; }
  return statusTransitions[fromStatus]?.includes(toStatus) || false;
}

export function getValidNextStatuses(currentStatus: TaskStatus): TaskStatus[] {
  return statusTransitions[currentStatus] || [];
}

// Priority scoring for sorting
const priorityScores: Record<TaskPriority, number> = {
  [TaskPriority.Critical]: 6,
  [TaskPriority.Highest]: 5,
  [TaskPriority.High]: 4,
  [TaskPriority.Medium]: 3,
  [TaskPriority.Low]: 2,
  [TaskPriority.Lowest]: 1
};

const urgencyScores: Record<TaskUrgency, number> = {
  [TaskUrgency.Urgent]: 3,
  [TaskUrgency.Normal]: 2,
  [TaskUrgency.Low]: 1
};

export function calculateTaskScore(priority: TaskPriority, urgency: TaskUrgency): number {
  return (priorityScores[priority] || 0) * 10 + (urgencyScores[urgency] || 0);
}

// Due date validation and overdue checking
export function isOverdue(dueDate: string | Date | null | undefined): boolean {
  if (!dueDate) { return false; }
  const due = new Date(dueDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Compare dates only, not times
  due.setHours(0, 0, 0, 0);
  return due < now;
}

export function isDueToday(dueDate: string | Date | null | undefined): boolean {
  if (!dueDate) { return false; }
  const due = new Date(dueDate);
  const now = new Date();
  return due.toDateString() === now.toDateString();
}

export function isDueSoon(dueDate: string | Date | null | undefined, daysThreshold = 3): boolean {
  if (!dueDate) { return false; }
  const due = new Date(dueDate);
  const now = new Date();
  const diffTime = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 && diffDays <= daysThreshold;
}

// Task sorting comparators
export interface Task {
  id: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  urgency: TaskUrgency;
  dueDate?: string | null;
  sortOrder?: number;
  createdAt: string;
  updatedAt: string;
  assignee?: {
    id: number;
    name: string;
  };
}

export type SortField = 'priority' | 'dueDate' | 'createdAt' | 'title' | 'status' | 'assignee';
export type SortOrder = 'asc' | 'desc';

export function sortTasks(tasks: Task[], sortField: SortField, sortOrder: SortOrder = 'asc'): Task[] {
  const sorted = [...tasks].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'priority':
        const scoreA = calculateTaskScore(a.priority, a.urgency);
        const scoreB = calculateTaskScore(b.priority, b.urgency);
        comparison = scoreB - scoreA; // Higher score first
        break;

      case 'dueDate':
        if (!a.dueDate && !b.dueDate) { comparison = 0; }
        else if (!a.dueDate) { comparison = 1; }
        else if (!b.dueDate) { comparison = -1; }
        else { comparison = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(); }
        break;

      case 'createdAt':
        comparison = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        break;

      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;

      case 'status':
        const statusOrder = [TaskStatus.InProgress, TaskStatus.InReview, TaskStatus.Todo, TaskStatus.Blocked, TaskStatus.Done, TaskStatus.Cancelled];
        comparison = statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
        break;

      case 'assignee':
        const nameA = a.assignee?.name || 'zzz'; // Unassigned at end
        const nameB = b.assignee?.name || 'zzz';
        comparison = nameA.localeCompare(nameB);
        break;
    }

    // Apply sort order
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  return sorted;
}

// Task filtering
export interface TaskFilters {
  status?: TaskStatus[];
  priority?: TaskPriority[];
  urgency?: TaskUrgency[];
  assigneeId?: number | null;
  categoryId?: string;
  entityType?: string;
  entityId?: string;
  isOverdue?: boolean;
  isDueToday?: boolean;
  isDueSoon?: boolean;
  searchText?: string;
  tags?: string[];
}

export function filterTasks(tasks: Task[], filters: TaskFilters): Task[] {
  return tasks.filter(task => {
    // Status filter
    if (filters.status?.length && !filters.status.includes(task.status)) {
      return false;
    }

    // Priority filter
    if (filters.priority?.length && !filters.priority.includes(task.priority)) {
      return false;
    }

    // Urgency filter
    if (filters.urgency?.length && !filters.urgency.includes(task.urgency)) {
      return false;
    }

    // Assignee filter
    if (filters.assigneeId !== undefined) {
      if (filters.assigneeId === null && task.assignee) { return false; }
      if (filters.assigneeId !== null && task.assignee?.id !== filters.assigneeId) { return false; }
    }

    // Due date filters
    if (filters.isOverdue && !isOverdue(task.dueDate)) { return false; }
    if (filters.isDueToday && !isDueToday(task.dueDate)) { return false; }
    if (filters.isDueSoon && !isDueSoon(task.dueDate)) { return false; }

    // Search text filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      const titleMatch = task.title.toLowerCase().includes(searchLower);
      const descriptionMatch = (task as any).description?.toLowerCase().includes(searchLower);
      if (!titleMatch && !descriptionMatch) { return false; }
    }

    return true;
  });
}

// Bulk operations
export function canBulkUpdateStatus(tasks: Task[], newStatus: TaskStatus): boolean {
  return tasks.every(task => isValidStatusTransition(task.status, newStatus));
}

export function getCommonValidStatuses(tasks: Task[]): TaskStatus[] {
  if (tasks.length === 0) { return []; }

  const validStatusSets = tasks.map(task => new Set(getValidNextStatuses(task.status)));

  // Find intersection of all valid statuses
  const commonStatuses = Array.from(validStatusSets[0]).filter(status =>
    validStatusSets.every(set => set.has(status))
  );

  return commonStatuses;
}

// Task assignment validation
export function canAssignTask(task: Task, userId: number): boolean {
  // Can't assign completed or cancelled tasks
  if ([TaskStatus.Done, TaskStatus.Cancelled].includes(task.status)) {
    return false;
  }

  // Can reassign if already assigned to someone else
  if (task.assignee && task.assignee.id === userId) {
    return false; // Already assigned to this user
  }

  return true;
}

// Task completion helpers
export function canCompleteTask(task: Task): boolean {
  // Must be in review status to complete
  return task.status === TaskStatus.InReview;
}

export function canStartTask(task: Task): boolean {
  // Can only start Todo tasks
  return task.status === TaskStatus.Todo;
}

// Task statistics
export interface TaskStats {
  total: number;
  byStatus: Record<TaskStatus, number>;
  byPriority: Record<TaskPriority, number>;
  overdue: number;
  dueToday: number;
  dueSoon: number;
  unassigned: number;
}

export function calculateTaskStats(tasks: Task[]): TaskStats {
  const stats: TaskStats = {
    total: tasks.length,
    byStatus: {} as Record<TaskStatus, number>,
    byPriority: {} as Record<TaskPriority, number>,
    overdue: 0,
    dueToday: 0,
    dueSoon: 0,
    unassigned: 0
  };

  // Initialize counters
  Object.values(TaskStatus).forEach(status => {
    stats.byStatus[status] = 0;
  });
  Object.values(TaskPriority).forEach(priority => {
    stats.byPriority[priority] = 0;
  });

  // Calculate stats
  tasks.forEach(task => {
    stats.byStatus[task.status]++;
    stats.byPriority[task.priority]++;

    if (!task.assignee) { stats.unassigned++; }
    if (isOverdue(task.dueDate)) { stats.overdue++; }
    if (isDueToday(task.dueDate)) { stats.dueToday++; }
    if (isDueSoon(task.dueDate)) { stats.dueSoon++; }
  });

  return stats;
}

// Task board column helpers
export interface BoardColumn {
  id: string;
  name: string;
  statusFilter: TaskStatus[];
  maxTasks?: number;
  color: string;
}

export function getDefaultBoardColumns(): BoardColumn[] {
  return [
    {
      id: 'todo',
      name: 'To Do',
      statusFilter: [TaskStatus.Todo],
      color: '#94A3B8'
    },
    {
      id: 'in-progress',
      name: 'In Progress',
      statusFilter: [TaskStatus.InProgress],
      maxTasks: 5,
      color: '#3B82F6'
    },
    {
      id: 'in-review',
      name: 'In Review',
      statusFilter: [TaskStatus.InReview],
      color: '#F59E0B'
    },
    {
      id: 'blocked',
      name: 'Blocked',
      statusFilter: [TaskStatus.Blocked],
      color: '#EF4444'
    },
    {
      id: 'done',
      name: 'Done',
      statusFilter: [TaskStatus.Done],
      color: '#10B981'
    }
  ];
}

export function canMoveTaskToColumn(task: Task, column: BoardColumn, currentColumnTasks: Task[]): {
  canMove: boolean;
  reason?: string;
} {
  // Check if any of the column's statuses are valid transitions
  const targetStatus = column.statusFilter[0]; // Use first status as target
  if (!isValidStatusTransition(task.status, targetStatus)) {
    return {
      canMove: false,
      reason: `Cannot move task from ${task.status} to ${targetStatus}`
    };
  }

  // Check WIP limit
  if (column.maxTasks && currentColumnTasks.length >= column.maxTasks) {
    return {
      canMove: false,
      reason: `Column "${column.name}" is at its limit of ${column.maxTasks} tasks`
    };
  }

  return { canMove: true };
}

// Smart task suggestions
export function suggestNextTasks(completedTask: Task, allTasks: Task[]): Task[] {
  // Find tasks that might be unblocked or related
  const suggestions: Task[] = [];

  // 1. Tasks in the same category that are Todo
  if ((completedTask as any).categoryId) {
    const sameCategoryTodos = allTasks.filter(task =>
      (task as any).categoryId === (completedTask as any).categoryId &&
      task.status === TaskStatus.Todo &&
      task.id !== completedTask.id
    );
    suggestions.push(...sameCategoryTodos.slice(0, 2));
  }

  // 2. High priority todos
  const highPriorityTodos = allTasks.filter(task =>
    task.status === TaskStatus.Todo &&
    [TaskPriority.Critical, TaskPriority.Highest, TaskPriority.High].includes(task.priority) &&
    !suggestions.find(s => s.id === task.id)
  );
  suggestions.push(...highPriorityTodos.slice(0, 3 - suggestions.length));

  // 3. Overdue or due today tasks
  const urgentTasks = allTasks.filter(task =>
    task.status === TaskStatus.Todo &&
    (isOverdue(task.dueDate) || isDueToday(task.dueDate)) &&
    !suggestions.find(s => s.id === task.id)
  );
  suggestions.push(...urgentTasks.slice(0, 5 - suggestions.length));

  return suggestions.slice(0, 3); // Return top 3 suggestions
}

// Recurrence Helpers
export function isVirtualTask(task: { id: string }): boolean {
  return task.id.startsWith('virtual-');
}

export function getParentRecurrenceTaskID(virtualTaskId: string): string | null {
  if (!virtualTaskId.startsWith('virtual-')) {
    return null;
  }

  // Format: virtual-<ParentID>-<Timestamp>
  // We need to extract <ParentID>
  const parts = virtualTaskId.split('-');
  if (parts.length < 3) {
    return null;
  }

  return parts.slice(1, -1).join('-');
}
